/** @jsxImportSource @emotion/react */
import {
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { canResendInvite } from '@sportninja/common/features/competition/operations/utils/rosterRows'
import type { InviteStatus } from '@sportninja/common/features/competition/operations/utils/rosterRows'
import RowActionsCell from '../cells/RowActionsCell'

const translate = t as (key: string, options?: unknown) => string

export type RosterRowAction = 'edit' | 'resend' | 'remove'

interface RosterRowActionsProps {
  row: { name: string; inviteStatus: InviteStatus }
  onSelect: (action: RosterRowAction) => void
  /**
   * Noun for the edit and delete labels — 'player' or 'teamOfficial'. The menu
   * is shared with the officials grid, so a hardcoded "Edit Player" would
   * misname the row there.
   */
  entity?: 'player' | 'teamOfficial'
}

/**
 * Edit / Resend invitation / Delete for a roster row,
 * in the same ⋯ menu the other Operations grids use.
 *
 * Resend appears only for someone invited who has not accepted — matching
 * `InviteAction`'s own rule, so the menu never offers a resend the API would
 * reject.
 */
const RosterRowActions = ({
  row,
  onSelect,
  entity = 'player',
}: RosterRowActionsProps) => {
  'use memo'

  const items: MenuProps['items'] = [
    // A player opens Player Details (read first, edit per section), labelled
    // and iconed like the Player page's button; an official still edits.
    entity === 'player'
      ? {
          key: 'edit',
          icon: <SafetyCertificateOutlined />,
          label: capitalizeWords('Player Details'),
        }
      : {
          key: 'edit',
          icon: <EditOutlined />,
          label: capitalizeWords(
            translate('common:editEntity', {
              entity: translate(`common:${entity}`),
            })
          ),
        },
    ...(canResendInvite(row.inviteStatus)
      ? [
          {
            key: 'resend',
            icon: <MailOutlined />,
            label: capitalizeWords(translate('common:resendInvite')),
          },
        ]
      : []),
    {
      key: 'remove',
      icon: <DeleteOutlined />,
      label: capitalizeWords(
        `${translate('common:delete')} ${translate(`common:${entity}`)}`
      ),
      danger: true,
    },
  ]

  return (
    <RowActionsCell
      items={items}
      onSelect={(key) => onSelect(key as RosterRowAction)}
      label={`${row.name} actions`}
    />
  )
}

export default RosterRowActions
